/**
 * Static MOE Primary (P1-P6) syllabus topic tables. Imported only by the
 * primary page builder, so it stays out of the secondary/exam page chunks.
 */
import type { SyllabusTopic } from './types';

/**
 * MOE Primary Mathematics (P1–P6) core topics by level, as chapter + items.
 * Used for Primary 1–6 hub pages. Update when syllabus changes.
 */
export const PRIMARY_TOPICS_BY_LEVEL: Record<number, SyllabusTopic[]> = {
  1: [
    {
      category: 'Chapter 1: Numbers to 10',
      items: ['Counting to 10', 'Comparing Numbers'],
    },
    { category: 'Chapter 2: Addition', items: ['Addition Stories'] },
    {
      category: 'Chapter 3: Subtraction',
      items: ['Subtraction Stories', 'Family of Facts'],
    },
    {
      category: 'Chapter 4: Shapes',
      items: [
        'Common Shapes',
        'Getting to Know Shapes',
        'Grouping Shapes',
        'Half Circle and Quarter Circle',
        'Forming Shapes',
        'Copying Figures',
      ],
    },
    { category: 'Chapter 5: Ordinal Numbers', items: ['Naming Positions'] },
    {
      category: 'Chapter 6: Numbers to 20',
      items: ['Counting to 20', 'Comparing and Ordering Numbers'],
    },
    {
      category: 'Chapter 7: Addition and Subtraction',
      items: [
        'Methods of Addition',
        'Methods of Subtraction',
        'Methods of Addition and Subtraction',
      ],
    },
    {
      category: 'Chapter 8: Picture Graphs',
      items: ['Making and Reading Picture Graphs'],
    },
    {
      category: 'Chapter 9: Numbers to 100',
      items: [
        'Counting to 100',
        'Number Patterns',
        'Comparing and Ordering Numbers',
      ],
    },
    {
      category: 'Chapter 10: Addition and Subtraction within 100',
      items: [
        'Addition without Renaming',
        'Addition with Renaming',
        'Addition of Three Numbers',
        'Subtraction without Renaming',
        'Subtraction with Renaming',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 11: Length',
      items: [
        'Measuring Length in Centimetres',
        'Comparing and Ordering Length',
        'Curves and Straight Lines',
      ],
    },
    {
      category: 'Chapter 12: Multiplication',
      items: [
        'Getting Ready to Multiply',
        'Adding Equal Groups',
        'Multiplication Stories',
        'Solving Multiplication Word Problems',
      ],
    },
    {
      category: 'Chapter 13: Division',
      items: [
        'Equal Sharing',
        'Equal Grouping',
        'Division Stories',
        'Solving Division Word Problems',
      ],
    },
    {
      category: 'Chapter 14: Time',
      items: [
        'Telling Time to 5 Minutes',
        'Telling Time Using am and pm',
        'One Hour and Half an Hour',
      ],
    },
    {
      category: 'Chapter 15: Money',
      items: [
        'Counting Coins and Notes',
        'Exchanging Money',
        'Comparing Amounts of Money',
        'Adding and Subtracting Money',
      ],
    },
  ],
  2: [
    {
      category: 'Chapter 1: Numbers to 1000',
      items: [
        'Counting',
        'Hundreds, Tens and Ones',
        'Comparing and Ordering Numbers',
        'Number Patterns',
      ],
    },
    {
      category: 'Chapter 2: Addition and Subtraction within 1000',
      items: [
        'Addition and Subtraction',
        'Addition without Renaming',
        'Addition with Renaming',
        'Subtraction without Renaming',
        'Subtraction with Renaming',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 3: Length',
      items: [
        'Measuring Length in Metres',
        'Comparing and Ordering Lengths',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 4: Multiplication and Division',
      items: [
        'Multiplication',
        'Division',
        'Equal Sharing',
        'Equal Grouping',
        'Multiplication and Division',
      ],
    },
    {
      category: 'Chapter 5: Multiplication Tables of 2, 5 and 10',
      items: [
        'Multiplication Table of 2',
        '2 More and 2 Less',
        'Multiplying and Dividing by 2',
        'Multiplication Table of 5',
        '5 More and 5 Less',
        'Multiplying and Dividing by 5',
        'Multiplication Table of 10',
        '10 More and 10 Less',
        'Multiplying and Dividing by 10',
      ],
    },
    {
      category: 'Chapter 6: Mass',
      items: [
        'Mass',
        'Measuring Mass in Kilograms',
        'Measuring Mass in Grams',
        'Comparing and Ordering Masses',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 7: Time',
      items: [
        'Telling Time to the Minute',
        'Measuring Time in Hours and Minutes',
        'Converting Time',
      ],
    },
    {
      category: 'Chapter 8: Addition and Subtraction',
      items: ['Word Problems', 'More Word Problems'],
    },
    {
      category: 'Chapter 9: Multiplication Tables of 3 and 4',
      items: [
        'Multiplication Table of 3',
        '3 More and 3 Less',
        'Multiplying and Dividing by 3',
        'Multiplication Table of 4',
        '4 More and 4 Less',
        'Multiplying and Dividing by 4',
      ],
    },
    {
      category: 'Chapter 10: Money',
      items: [
        'Dollars and Cents',
        'Converting Money',
        'Comparing Money',
        'Word Problems (Addition and Subtraction)',
        'Word Problems (Multiplication and Division)',
      ],
    },
    {
      category: 'Chapter 11: Fractions',
      items: [
        'Halves and Quarters',
        'More About Fractions',
        'Comparing and Ordering Fractions',
        'Addition and Subtraction of Fractions',
      ],
    },
    {
      category: 'Chapter 12: Volume',
      items: [
        'Volume',
        'Measuring Volume in Litres',
        'Comparing and Ordering Volumes in Litres',
        'Word Problems (Addition and Subtraction)',
        'Word Problems (Multiplication and Division)',
      ],
    },
    {
      category: 'Chapter 13: Picture Graphs',
      items: ['Making Picture Graphs', 'Tally Chart'],
    },
    {
      category: 'Chapter 14: Shapes',
      items: [
        'Making Patterns with Shapes',
        '3-Dimension Shapes',
        'Getting to Know 3-Dimension Shapes',
        'Building Figures with 3-Dimension Shapes',
        'Making Patterns with 3-Dimension Shapes',
      ],
    },
  ],
  3: [
    {
      category: 'Chapter 1: Numbers to 10 000',
      items: [
        'Counting',
        'Thousands, Hundreds, Tens and Ones',
        'Comparing and Ordering Numbers',
        'Number Patterns',
      ],
    },
    {
      category: 'Chapter 2: Addition and Subtraction',
      items: [
        'Sum and Difference',
        'Addition',
        'Subtraction',
        'Mental Calculation',
      ],
    },
    {
      category: 'Chapter 3: Money',
      items: [
        'Making up $1, $10 or $100',
        'Adding Money',
        'Subtracting Money',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 4: Multiplication Tables of 6, 7, 8 and 9',
      items: [
        'Multiplication Table of 6',
        'Multiplying and Dividing by 6',
        'Multiplication Table of 7',
        'Multiplying and Dividing by 7',
        'Multiplication Table of 8',
        'Multiplying and Dividing by 8',
        'Multiplication Table of 9',
        'Multiplying and Dividing by 9',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 5: Multiplication and Division',
      items: [
        'Multiplication',
        'Division without Remainder',
        'Division with Remainder',
      ],
    },
    {
      category: 'Chapter 6: More Word Problems',
      items: ['Addition and Subtraction', 'Four Operations'],
    },
    { category: 'Chapter 7: Bar Graphs', items: ['Bar Graphs'] },
    {
      category: 'Chapter 8: Angles',
      items: ['Concept of Angles', 'Acute Angle, Right Angle and Obtuse Angle'],
    },
    {
      category: 'Chapter 9: Perpendicular and Parallel Lines',
      items: [
        'Perpendicular Lines',
        'Drawing Perpendicular Lines',
        'Parallel Lines',
        'Drawing Parallel Lines',
        'Horizontal and Vertical Lines',
      ],
    },
    {
      category: 'Chapter 10: Fractions',
      items: [
        'Equivalent Fractions',
        'Simplifying Fractions',
        'Comparing and Ordering Fractions',
        'Addition and Subtraction of Fractions',
      ],
    },
    {
      category: 'Chapter 11: Length, Mass and Volume',
      items: [
        'Measuring Length in Metres and Centimetres',
        'Measuring Length in Kilometres and Metres',
        'Measuring Mass in Kilograms and Grams',
        'Measuring Volume in Millilitres',
        'Measuring Volume in Litres and Millilitres',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 12: Area and Perimeter',
      items: [
        'Area in Square Units',
        'Area in Square Centimetres',
        'Area in Square Metres',
        'Perimeter in Centimetres',
        'Perimeter in Metres',
        'Area and Perimeter of Squares and Rectangles',
      ],
    },
    {
      category: 'Chapter 13: Time',
      items: [
        'Time in Seconds',
        '24-hour Clock',
        'Starting Time, Finishing Time and Duration',
      ],
    },
  ],
  4: [
    {
      category: 'Chapter 1: Numbers to 100 000',
      items: [
        'Counting, Reading and Writing Numbers',
        'Ten Thousands, Thousands, Hundreds, Tens and Ones',
        'Comparing and Ordering Numbers',
        'Number Patterns',
        'Rounding Numbers to the Nearest 10',
        'Rounding Numbers to the Nearest 100',
        'Rounding Numbers to the Nearest 1000',
      ],
    },
    {
      category: 'Chapter 2: Factors and Multiples',
      items: ['Factors', 'Common Factors', 'Multiples', 'Common Multiples'],
    },
    {
      category: 'Chapter 3: Four Operations of Whole Numbers',
      items: [
        'Multiplication by 1-Digit Number',
        'Multiplication by 2-Digit Number',
        'Division by 1-Digit Number',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 4: Tables and Line Graphs',
      items: ['Tables', 'Line Graphs'],
    },
    {
      category: 'Chapter 5: Fractions (I)',
      items: [
        'Mixed Numbers',
        'Improper Fractions',
        'Mixed Numbers and Improper Fractions',
        'Comparing and Ordering Fractions',
      ],
    },
    {
      category: 'Chapter 6: Fractions (II)',
      items: [
        'Addition and Subtraction of Fractions',
        'Word Problems',
        'Fraction of a Set',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 7: Angles',
      items: ['Naming Angles', 'Measuring Angles', 'Drawing Angles'],
    },
    {
      category: 'Chapter 8: Rectangles and Squares',
      items: [
        'Properties of Rectangles and Squares',
        'Drawing Rectangles and Squares',
      ],
    },
    {
      category: 'Chapter 9: Decimals',
      items: [
        'Tenths',
        'Hundredths',
        'Thousandths',
        'Comparing and Ordering Decimals',
        'Number Patterns',
        'Rounding Decimals',
        'Expressing a Fraction as a Decimal',
        'Expressing a Decimal as a Fraction',
      ],
    },
    {
      category: 'Chapter 10: Four Operations of Decimals',
      items: [
        'Addition of Decimals',
        'Subtraction of Decimals',
        'Word Problems',
        'Multiplication of Decimals (by 1-digit Number)',
        'Division of Decimals (by 1-digit Number)',
        'Word Problems',
      ],
    },
    { category: 'Chapter 11: Pie Charts', items: ['Pie Charts'] },
    {
      category: 'Chapter 12: Area and Perimeter',
      items: [
        'Finding the Length of Squares',
        'Finding Unknown Sides of Rectangles',
        'Perimeter of Composite Figures',
        'Area of Composite Figures',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 13: Nets',
      items: [
        'Geometric Figures',
        'Drawing Geometric Figures on Isometric Grids',
        'Identifying Nets of Geometric Figures',
      ],
    },
    {
      category: 'Chapter 14: Symmetry',
      items: [
        'Symmetric Figures and Lines of Symmetry',
        'Completing Symmetric Figures on Grids',
      ],
    },
  ],
  5: [
    {
      category: 'Chapter 1: Numbers to 10 Million',
      items: ['Numbers to 10 Million'],
    },
    {
      category: 'Chapter 2: Four Operations of Whole Numbers',
      items: [
        'Multiplying by 10, 100 and 1000',
        'Multiplying by Tens, Hundreds and Thousands',
        'Dividing by 10, 100 and 1000',
        'Dividing by Tens, Hundreds and Thousands',
        'Order of Operations',
        'Order of Operations with Use of Brackets',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 3: Fraction and Division',
      items: [
        'Division of Whole Numbers as Fractions',
        'Expressing Fractions as Decimals',
      ],
    },
    {
      category: 'Chapter 4: Four Operations of Fractions',
      items: [
        'Addition and Subtraction of Mixed Numbers',
        'Word Problems (Addition and Subtraction)',
        'Multiplying a Fraction and a Whole Number',
        'Multiplying Two Fractions',
        'Multiplying a Mixed Number and a Whole Number',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 5: Area of Triangle',
      items: [
        'Base and Height of a Triangle',
        'Area of Triangle',
        'Area of Composite Figures',
      ],
    },
    {
      category: 'Chapter 6: Volume',
      items: [
        'Volume of Solids in Cubic Units',
        'Volume of Solids in Cubic Centimetres (cm³) and Cubic Metres (m³)',
        'Drawing Cubes and Cuboids on Isometric Grids',
        'Drawing Different Views of Solids on Square Grids',
        'Volume of Cubes and Cuboids',
        'Volume of Liquids',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 7: Decimals',
      items: [
        'Multiplying by 10, 100 and 1000',
        'Multiplying by Tens, Hundreds and Thousands',
        'Dividing by 10, 100 and 1000',
        'Dividing by Tens, Hundreds and Thousands',
        'Converting Measurements',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 8: Rate',
      items: ['Rate', 'Word Problems'],
    },
    {
      category: 'Chapter 9: Percentage',
      items: [
        'Percent',
        'Conversions between Fractions, Decimals and Percentages',
        'Percentage Part of a Whole',
        'Percentage in Pie Charts',
        'GST, Discount and Annual Interest',
      ],
    },
    {
      category: 'Chapter 10: Angles',
      items: [
        'Angles on a Straight Line',
        'Vertically Opposite Angles',
        'Angles at a Point',
        'Finding Unknown Angles',
      ],
    },
    {
      category: 'Chapter 11: Properties of Triangles',
      items: [
        'Types of Triangles',
        'Angle Sum of a Triangle',
        'Finding Unknown Angles',
        'Drawing Triangles',
      ],
    },
    {
      category:
        'Chapter 12: Properties of Parallelogram, Rhombus and Trapezium',
      items: [
        'Properties of Parallelogram',
        'Properties of Rhombus',
        'Properties of Trapezium',
        'Finding Unknown Angles',
        'Drawing 4-sided Figures',
      ],
    },
  ],
  6: [
    {
      category: 'Chapter 1: Fractions',
      items: [
        'Dividing a Fraction by a Whole Number',
        'Dividing a Whole Number by a Fraction',
        'Dividing a Fraction by a Fraction',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 2: Ratio',
      items: [
        'Ratio',
        'Equivalent Ratios',
        'Finding Equivalent Ratios',
        'Finding New Ratios',
        'Fraction and Ratio',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 3: Percentage',
      items: [
        'Finding the Whole Given a Part and the Percentage',
        'Percentage Increase and Percentage Decrease',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 4: Angles in Geometrical Figures',
      items: ['Finding Unknown Angles'],
    },
    {
      category: 'Chapter 5: Circles',
      items: [
        'Parts of a Circle',
        'Circumference of a Circle',
        'Perimeter of a Semicircle and a Quarter Circle',
        'Area of a Circle',
        'Area of a Semicircle and a Quarter Circle',
        'Area and Perimeter of Composite Figures',
      ],
    },
    {
      category: 'Chapter 6: Volume of Cube and Cuboid',
      items: [
        'Finding the Side of a Cube or Cuboid',
        'Finding the Area of One Face of a Cube or Cuboid',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 7: Average',
      items: [
        'Finding Average',
        'Finding Total Value',
        'Finding Number of Data',
        'Word Problems',
      ],
    },
    {
      category: 'Chapter 8: Algebra',
      items: [
        'Algebraic Expressions',
        'Simplifying and Evaluating Algebraic Expressions',
        'Solving Algebraic Equations',
      ],
    },
  ],
};
